import { t } from "@hakili/i18n";
import { Plus } from "lucide-react";

/**
 * `REG-PROFILE-001`, Arc B, `M1`: **the client's declared profile, as a form.**
 *
 * Step 0 of `docs/claude/underwriter-workflow.md`: the borrower's declared profile is « entered at registration,
 * kept on the profile, updated by the client over time, never re-asked dossier by dossier ». One form serves both
 * moments: the registration screen (`ProfilInscription`, WF-CLI-01 step 4) embeds {@link DeclaredProfileFields}
 * and so does the profile screen (`ProfilDeclare`), and both talk to `GET`/`PUT /api/v1/me/profile`.
 *
 * ## The rules live here, with no DOM
 *
 * {@link validateDraft} and {@link toWrite} are plain functions of a draft, so they are tested without rendering
 * and cannot differ between the two screens. They mirror what `domain/client-profile.ts` enforces at the door, so
 * a client is told BEFORE the round trip; the API refuses whatever they let through, and its sentence is shown.
 * Dates in the future are the door's to refuse: this app has no `Clock` to inject (`9.2.4` D2).
 *
 * ## Q13, ruled 9 October 2026: no default, no empty state silently accepted
 *
 * « Aucun engagement en cours » and « J'ai des engagements en cours » are two controls and NEITHER is selected
 * until the client chooses. An empty list is not an answer: `commitmentChoice` starts as `null`, `null` is a
 * validation error with its own sentence, and the submit control is NEVER `inactive` for that reason, because a
 * control that is shut with no message is the very silence the ruling forbids. The press explains.
 *
 * ## What is deliberately absent
 *
 * No deposit (apport). Step 0: « the amount is not known until the client has chosen a specific vehicle », so it
 * is captured at the dossier. And no default contract type or type of credit: a default is an answer about a
 * person that the person never gave.
 */

export type ContractType = "fonctionnaireTitulaire" | "cdi" | "cdd" | "contractuelEtat" | "autre";
export type CreditType = "pretBancaire" | "microfinance" | "avanceEmployeur";

/** The canon's five contract types, in Step 0's order. */
export const CONTRACT_TYPES: readonly ContractType[] = [
  "fonctionnaireTitulaire",
  "cdi",
  "cdd",
  "contractuelEtat",
  "autre",
];
/** The canon's three types of credit, in Step 0's order. */
export const CREDIT_TYPES: readonly CreditType[] = ["pretBancaire", "microfinance", "avanceEmployeur"];

/**
 * Spelled with literal catalogue keys, not built from the value: the catalogue's own guard reads keys it can
 * SEE, and a key assembled in a template would hide from it (`09-01`).
 */
const CONTRACT_LABELS = new Map<ContractType, string>([
  ["fonctionnaireTitulaire", "client.declaration.contratFonctionnaireTitulaire"],
  ["cdi", "client.declaration.contratCdi"],
  ["cdd", "client.declaration.contratCdd"],
  ["contractuelEtat", "client.declaration.contratContractuelEtat"],
  ["autre", "client.declaration.contratAutre"],
]);
const CREDIT_LABELS = new Map<CreditType, string>([
  ["pretBancaire", "client.declaration.creditPretBancaire"],
  ["microfinance", "client.declaration.creditMicrofinance"],
  ["avanceEmployeur", "client.declaration.creditAvanceEmployeur"],
]);

/** What `GET /api/v1/me/profile` answers. */
export interface ProfileView {
  readonly fullName: string;
  readonly phone: string;
  readonly city: string | null;
  readonly dateOfBirth: string | null;
  readonly declaredAddress: string | null;
  readonly employments: readonly {
    readonly id: string;
    readonly employer: string;
    readonly position: string;
    readonly contractType: ContractType;
    readonly dateOfHire: string | null;
    readonly declaredSeniorityMonths: number | null;
    readonly monthlyNetSalary: number;
  }[];
  readonly commitments: readonly {
    readonly id: string;
    readonly lenderName: string;
    readonly creditType: CreditType;
    readonly outstandingBalance: number;
    readonly balanceAsOfDate: string;
    readonly monthlyInstalment: number;
    readonly loanStartDate: string | null;
    readonly loanExpectedEndDate: string | null;
  }[];
  readonly noCommitmentsDeclared: boolean;
  /** `unfilled` (nothing said), `declared` (rows) or `none` (« aucun engagement en cours »). */
  readonly commitmentsSection: "unfilled" | "declared" | "none";
}

export interface EmploymentWrite {
  readonly employer: string;
  readonly position: string;
  readonly contractType: ContractType;
  readonly dateOfHire?: string;
  readonly declaredSeniorityMonths?: number;
  readonly monthlyNetSalary: number;
}

export interface CommitmentWrite {
  readonly lenderName: string;
  readonly creditType: CreditType;
  readonly outstandingBalance: number;
  readonly balanceAsOfDate: string;
  readonly monthlyInstalment: number;
  readonly loanStartDate?: string;
  readonly loanExpectedEndDate?: string;
}

/** What `PUT /api/v1/me/profile` is given by the form. The door refuses any key not listed here. */
export interface ProfileWrite {
  readonly dateOfBirth: string | null;
  readonly declaredAddress: string | null;
  readonly employments: readonly EmploymentWrite[];
  readonly commitments: readonly CommitmentWrite[];
  readonly noCommitmentsDeclared: boolean;
}

export interface EmploymentDraft {
  employer: string;
  position: string;
  contractType: ContractType | "";
  dateOfHire: string;
  seniorityMonths: string;
  salary: string;
}

export interface CommitmentDraft {
  lenderName: string;
  creditType: CreditType | "";
  balance: string;
  balanceDate: string;
  instalment: string;
  startDate: string;
  endDate: string;
}

/** `null` is « not answered yet » and is never an answer (Q13). */
export type CommitmentChoice = "has" | "none" | null;

export interface ProfileDraft {
  dateOfBirth: string;
  address: string;
  employments: EmploymentDraft[];
  commitmentChoice: CommitmentChoice;
  commitments: CommitmentDraft[];
}

export const emptyEmployment = (): EmploymentDraft => ({
  employer: "",
  position: "",
  contractType: "",
  dateOfHire: "",
  seniorityMonths: "",
  salary: "",
});

export const emptyCommitment = (): CommitmentDraft => ({
  lenderName: "",
  creditType: "",
  balance: "",
  balanceDate: "",
  instalment: "",
  startDate: "",
  endDate: "",
});

/** One empty employment to type into, and NO answer about commitments. */
export const emptyDraft = (): ProfileDraft => ({
  dateOfBirth: "",
  address: "",
  employments: [emptyEmployment()],
  commitmentChoice: null,
  commitments: [],
});

/** The draft a client edits, from what the door holds. « Unfilled » stays unanswered; it is never read as « none ». */
export function draftFromView(view: ProfileView): ProfileDraft {
  return {
    dateOfBirth: view.dateOfBirth ?? "",
    address: view.declaredAddress ?? "",
    employments:
      view.employments.length === 0
        ? [emptyEmployment()]
        : view.employments.map((one) => ({
            employer: one.employer,
            position: one.position,
            contractType: one.contractType,
            dateOfHire: one.dateOfHire ?? "",
            seniorityMonths: one.declaredSeniorityMonths === null ? "" : String(one.declaredSeniorityMonths),
            salary: String(one.monthlyNetSalary),
          })),
    commitmentChoice:
      view.commitmentsSection === "none" ? "none" : view.commitmentsSection === "declared" ? "has" : null,
    commitments: view.commitments.map((one) => ({
      lenderName: one.lenderName,
      creditType: one.creditType,
      balance: String(one.outstandingBalance),
      balanceDate: one.balanceAsOfDate,
      instalment: String(one.monthlyInstalment),
      startDate: one.loanStartDate ?? "",
      endDate: one.loanExpectedEndDate ?? "",
    })),
  };
}

const isDigits = (value: string): boolean => /^\d+$/u.test(value);

/** `YYYY-MM-DD` that is a REAL calendar day: « 1990-02-31 » is refused, not rolled into March. */
function isCalendarDate(value: string): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/u.exec(value);
  if (match === null) return false;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const probe = new Date(Date.UTC(year, month - 1, day));
  return probe.getUTCFullYear() === year && probe.getUTCMonth() === month - 1 && probe.getUTCDate() === day;
}

const filled = (value: string): boolean => value.trim() !== "";

export type DraftErrors = Readonly<Record<string, string>>;

/**
 * The form's refusals, keyed by where they belong: `dateOfBirth`, `commitmentChoice`, `employment.N` and
 * `commitment.N`. Empty means the draft can be sent. The sentences are the catalogue's, stored with the guard
 * and never written in a screen (`4.5.5`'s rule for the API, kept here for its mirror).
 */
export function validateDraft(draft: ProfileDraft): DraftErrors {
  const errors: Record<string, string> = {};

  if (filled(draft.dateOfBirth) && !isCalendarDate(draft.dateOfBirth)) {
    errors["dateOfBirth"] = t("client.declaration.erreurNaissance");
  }

  draft.employments.forEach((one, index) => {
    const hasHire = filled(one.dateOfHire);
    const hasSeniority = filled(one.seniorityMonths);
    const seniorityOk = !hasSeniority || isDigits(one.seniorityMonths);
    const hireOk = !hasHire || isCalendarDate(one.dateOfHire);
    const valid =
      filled(one.employer) &&
      filled(one.position) &&
      one.contractType !== "" &&
      isDigits(one.salary) &&
      Number(one.salary) > 0 &&
      (hasHire || hasSeniority) &&
      seniorityOk &&
      hireOk;
    if (!valid) errors[`employment.${String(index)}`] = t("client.declaration.erreurEmploi");
  });

  if (draft.commitmentChoice === null) {
    errors["commitmentChoice"] = t("client.declaration.engagementsObligatoire");
  } else if (draft.commitmentChoice === "has") {
    if (draft.commitments.length === 0) {
      errors["commitmentChoice"] = t("client.declaration.engagementsAjoutez");
    }
    draft.commitments.forEach((one, index) => {
      const hasStart = filled(one.startDate);
      const hasEnd = filled(one.endDate);
      const complete =
        filled(one.lenderName) &&
        one.creditType !== "" &&
        isDigits(one.balance) &&
        isCalendarDate(one.balanceDate) &&
        isDigits(one.instalment) &&
        (!hasStart || isCalendarDate(one.startDate)) &&
        (!hasEnd || isCalendarDate(one.endDate));
      if (!complete) {
        errors[`commitment.${String(index)}`] = t("client.declaration.erreurEngagement");
      } else if (hasStart && hasEnd && one.endDate < one.startDate) {
        errors[`commitment.${String(index)}`] = t("client.declaration.erreurDatesPret");
      }
    });
  }

  return errors;
}

/**
 * The write the door accepts. Call only on a draft {@link validateDraft} accepted.
 *
 * « Aucun engagement en cours » is `noCommitmentsDeclared: true` with an EMPTY list, even if blocks were typed
 * before he changed his mind: the door refuses the contradiction, and his last answer is the one that counts.
 * Empty optional fields are omitted, and an emptied birth date or address is `null` so a client can take back
 * what he declared.
 */
export function toWrite(draft: ProfileDraft): ProfileWrite {
  const has = draft.commitmentChoice === "has";
  return {
    dateOfBirth: filled(draft.dateOfBirth) ? draft.dateOfBirth : null,
    declaredAddress: filled(draft.address) ? draft.address.trim() : null,
    employments: draft.employments.map((one) => ({
      employer: one.employer.trim(),
      position: one.position.trim(),
      contractType: one.contractType as ContractType,
      ...(filled(one.dateOfHire) ? { dateOfHire: one.dateOfHire } : {}),
      ...(filled(one.seniorityMonths) ? { declaredSeniorityMonths: Number(one.seniorityMonths) } : {}),
      monthlyNetSalary: Number(one.salary),
    })),
    commitments: has
      ? draft.commitments.map((one) => ({
          lenderName: one.lenderName.trim(),
          creditType: one.creditType as CreditType,
          outstandingBalance: Number(one.balance),
          balanceAsOfDate: one.balanceDate,
          monthlyInstalment: Number(one.instalment),
          ...(filled(one.startDate) ? { loanStartDate: one.startDate } : {}),
          ...(filled(one.endDate) ? { loanExpectedEndDate: one.endDate } : {}),
        }))
      : [],
    noCommitmentsDeclared: !has,
  };
}

const FIELD = "w-full rounded-xl border border-gray-200 bg-white px-3 py-3 text-base";
const LABEL = "flex flex-col gap-1 text-sm text-gray-500";

function TextField({
  id,
  label,
  value,
  onChange,
  type = "text",
  numeric = false,
  autoComplete,
}: {
  readonly id: string;
  readonly label: string;
  readonly value: string;
  readonly onChange: (next: string) => void;
  readonly type?: "text" | "date";
  readonly numeric?: boolean;
  readonly autoComplete?: string;
}): React.JSX.Element {
  return (
    <div className={LABEL}>
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        data-testid={id}
        className={FIELD}
        type={type}
        {...(numeric ? { inputMode: "numeric" as const } : {})}
        {...(autoComplete === undefined ? {} : { autoComplete })}
        value={value}
        onChange={(event) => {
          onChange(numeric ? event.target.value.replaceAll(/\D/gu, "") : event.target.value);
        }}
      />
    </div>
  );
}

function BlockError({
  id,
  message,
}: {
  readonly id: string;
  readonly message: string | undefined;
}): React.JSX.Element | null {
  if (message === undefined) return null;
  return (
    <p role="alert" data-testid={id} className="text-sm text-rose-700">
      {message}
    </p>
  );
}

const numbered = (key: "client.declaration.emploi" | "client.declaration.engagement", n: number): string =>
  t(key).replace("{n}", String(n));

/**
 * The fields of the declared profile, controlled by the screen that embeds them.
 *
 * The parent owns the draft and the `attempted` flag: errors are COMPUTED from the draft on every render and shown
 * only once a submission was attempted, so they appear on the press and disappear the moment the client fixes
 * the thing, with no stale message left behind.
 */
export function DeclaredProfileFields({
  draft,
  onChange,
  attempted,
}: {
  readonly draft: ProfileDraft;
  readonly onChange: (next: ProfileDraft) => void;
  readonly attempted: boolean;
}): React.JSX.Element {
  const errors = attempted ? validateDraft(draft) : {};

  const setEmployment = (index: number, patch: Partial<EmploymentDraft>): void => {
    onChange({
      ...draft,
      employments: draft.employments.map((one, at) => (at === index ? { ...one, ...patch } : one)),
    });
  };
  const setCommitment = (index: number, patch: Partial<CommitmentDraft>): void => {
    onChange({
      ...draft,
      commitments: draft.commitments.map((one, at) => (at === index ? { ...one, ...patch } : one)),
    });
  };
  const choose = (choice: "has" | "none"): void => {
    onChange({
      ...draft,
      commitmentChoice: choice,
      // Saying he has some opens the first block at once, so the next thing on screen is the thing to fill.
      commitments:
        choice === "has" && draft.commitments.length === 0 ? [emptyCommitment()] : draft.commitments,
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <TextField
        id="declaration-naissance"
        label={t("client.declaration.naissance")}
        type="date"
        value={draft.dateOfBirth}
        autoComplete="bday"
        onChange={(next) => {
          onChange({ ...draft, dateOfBirth: next });
        }}
      />
      <BlockError id="declaration-naissance-erreur" message={errors["dateOfBirth"]} />
      <TextField
        id="declaration-adresse"
        label={t("client.declaration.adresse")}
        value={draft.address}
        autoComplete="street-address"
        onChange={(next) => {
          onChange({ ...draft, address: next });
        }}
      />

      <h2 className="text-sm font-semibold text-gray-900">{t("client.declaration.emplois")}</h2>
      <p
        data-testid="declaration-formel"
        className="rounded-xl bg-amber-50 px-3 py-2.5 text-xs leading-relaxed text-amber-700"
      >
        {t("client.declaration.formel")}
      </p>
      {draft.employments.map((one, index) => {
        const base = `emploi-${String(index)}`;
        return (
          <section
            key={base}
            data-testid={base}
            className="flex flex-col gap-3 rounded-2xl border border-gray-200 p-4"
          >
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-gray-900">
                {numbered("client.declaration.emploi", index + 1)}
              </h3>
              {index === 0 ? null : (
                <button
                  type="button"
                  data-testid={`${base}-retirer`}
                  className="text-sm font-medium text-rose-600"
                  onClick={() => {
                    onChange({ ...draft, employments: draft.employments.filter((_, at) => at !== index) });
                  }}
                >
                  {t("client.declaration.retirer")}
                </button>
              )}
            </div>
            <TextField
              id={`${base}-employeur`}
              label={t("client.declaration.employeur")}
              value={one.employer}
              autoComplete="organization"
              onChange={(next) => {
                setEmployment(index, { employer: next });
              }}
            />
            <TextField
              id={`${base}-poste`}
              label={t("client.declaration.poste")}
              value={one.position}
              autoComplete="organization-title"
              onChange={(next) => {
                setEmployment(index, { position: next });
              }}
            />
            <div className={LABEL}>
              <label htmlFor={`${base}-contrat`}>{t("client.declaration.contrat")}</label>
              {/* No type is pre-selected: a default contract type is an answer about a person nobody gave. */}
              <select
                id={`${base}-contrat`}
                data-testid={`${base}-contrat`}
                className={FIELD}
                value={one.contractType}
                onChange={(event) => {
                  setEmployment(index, { contractType: event.target.value as ContractType | "" });
                }}
              >
                <option value="">{t("client.declaration.choisir")}</option>
                {CONTRACT_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {t(CONTRACT_LABELS.get(type) ?? "client.declaration.contratAutre")}
                  </option>
                ))}
              </select>
            </div>
            <p className="text-xs text-gray-400">{t("client.declaration.ancienneteOu")}</p>
            <TextField
              id={`${base}-embauche`}
              label={t("client.declaration.embauche")}
              type="date"
              value={one.dateOfHire}
              onChange={(next) => {
                setEmployment(index, { dateOfHire: next });
              }}
            />
            <TextField
              id={`${base}-anciennete`}
              label={t("client.declaration.anciennete")}
              numeric
              value={one.seniorityMonths}
              onChange={(next) => {
                setEmployment(index, { seniorityMonths: next });
              }}
            />
            <TextField
              id={`${base}-salaire`}
              label={t("client.declaration.salaire")}
              numeric
              value={one.salary}
              onChange={(next) => {
                setEmployment(index, { salary: next });
              }}
            />
            <BlockError id={`${base}-erreur`} message={errors[`employment.${String(index)}`]} />
          </section>
        );
      })}
      <button
        type="button"
        data-testid="emploi-ajouter"
        className="flex items-center gap-1.5 self-start text-sm font-medium text-indigo-700"
        onClick={() => {
          onChange({ ...draft, employments: [...draft.employments, emptyEmployment()] });
        }}
      >
        <Plus className="h-4 w-4" aria-hidden="true" />
        {t("client.declaration.ajouterEmploi")}
      </button>

      <h2 id="engagements-titre" className="text-sm font-semibold text-gray-900">
        {t("client.declaration.engagements")}
      </h2>
      <p className="text-sm text-gray-500">{t("client.declaration.engagementsQuestion")}</p>
      {/* Two controls and NEITHER is selected until he chooses (Q13, ruled 9 October 2026). */}
      <div className="grid grid-cols-1 gap-2" role="group" aria-labelledby="engagements-titre">
        {(
          [
            ["has", "engagements-oui", "client.declaration.engagementsOui"],
            ["none", "engagements-aucun", "client.declaration.engagementsAucun"],
          ] as const
        ).map(([choice, testId, key]) => (
          <button
            key={choice}
            type="button"
            data-testid={testId}
            aria-pressed={draft.commitmentChoice === choice}
            className={`rounded-xl border px-3 py-3 text-left text-sm font-medium ${
              draft.commitmentChoice === choice
                ? "border-indigo-200 bg-indigo-50 text-indigo-700"
                : "border-gray-200 bg-white text-gray-600"
            }`}
            onClick={() => {
              choose(choice);
            }}
          >
            {t(key)}
          </button>
        ))}
      </div>
      <BlockError id="engagements-refus" message={errors["commitmentChoice"]} />

      {draft.commitmentChoice === "has"
        ? draft.commitments.map((one, index) => {
            const base = `engagement-${String(index)}`;
            return (
              <section
                key={base}
                data-testid={base}
                className="flex flex-col gap-3 rounded-2xl border border-gray-200 p-4"
              >
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-medium text-gray-900">
                    {numbered("client.declaration.engagement", index + 1)}
                  </h3>
                  {index === 0 ? null : (
                    <button
                      type="button"
                      data-testid={`${base}-retirer`}
                      className="text-sm font-medium text-rose-600"
                      onClick={() => {
                        onChange({
                          ...draft,
                          commitments: draft.commitments.filter((_, at) => at !== index),
                        });
                      }}
                    >
                      {t("client.declaration.retirer")}
                    </button>
                  )}
                </div>
                <TextField
                  id={`${base}-preteur`}
                  label={t("client.declaration.preteur")}
                  value={one.lenderName}
                  onChange={(next) => {
                    setCommitment(index, { lenderName: next });
                  }}
                />
                <div className={LABEL}>
                  <label htmlFor={`${base}-type`}>{t("client.declaration.typeCredit")}</label>
                  <select
                    id={`${base}-type`}
                    data-testid={`${base}-type`}
                    className={FIELD}
                    value={one.creditType}
                    onChange={(event) => {
                      setCommitment(index, { creditType: event.target.value as CreditType | "" });
                    }}
                  >
                    <option value="">{t("client.declaration.choisir")}</option>
                    {CREDIT_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {t(CREDIT_LABELS.get(type) ?? "client.declaration.creditPretBancaire")}
                      </option>
                    ))}
                  </select>
                </div>
                <TextField
                  id={`${base}-solde`}
                  label={t("client.declaration.solde")}
                  numeric
                  value={one.balance}
                  onChange={(next) => {
                    setCommitment(index, { balance: next });
                  }}
                />
                {/* The balance's OWN date, a control of its own: the founder's correction of 8 October 2026,
                    so Hakili can recompute what is owed today and judge the balance too old. */}
                <TextField
                  id={`${base}-date-solde`}
                  label={t("client.declaration.dateSolde")}
                  type="date"
                  value={one.balanceDate}
                  onChange={(next) => {
                    setCommitment(index, { balanceDate: next });
                  }}
                />
                <TextField
                  id={`${base}-mensualite`}
                  label={t("client.declaration.mensualite")}
                  numeric
                  value={one.instalment}
                  onChange={(next) => {
                    setCommitment(index, { instalment: next });
                  }}
                />
                <TextField
                  id={`${base}-debut`}
                  label={t("client.declaration.debutPret")}
                  type="date"
                  value={one.startDate}
                  onChange={(next) => {
                    setCommitment(index, { startDate: next });
                  }}
                />
                <TextField
                  id={`${base}-fin`}
                  label={t("client.declaration.finPrevue")}
                  type="date"
                  value={one.endDate}
                  onChange={(next) => {
                    setCommitment(index, { endDate: next });
                  }}
                />
                <BlockError id={`${base}-erreur`} message={errors[`commitment.${String(index)}`]} />
              </section>
            );
          })
        : null}
      {draft.commitmentChoice === "has" ? (
        <button
          type="button"
          data-testid="engagement-ajouter"
          className="flex items-center gap-1.5 self-start text-sm font-medium text-indigo-700"
          onClick={() => {
            onChange({ ...draft, commitments: [...draft.commitments, emptyCommitment()] });
          }}
        >
          <Plus className="h-4 w-4" aria-hidden="true" />
          {t("client.declaration.ajouterEngagement")}
        </button>
      ) : null}

      {/* The data's purpose, on both screens: it feeds every check, keeping it current is his, and it does not
          block him if it is later found inaccurate, because the real documents verify it at the dossier. */}
      <p
        data-testid="declaration-finalite"
        className="rounded-xl bg-gray-50 px-3 py-2.5 text-xs leading-relaxed text-gray-500"
      >
        {t("client.declaration.finalite")}
      </p>
    </div>
  );
}
